/**
 * AuditLogs Field Registry
 * Single source of truth for all audit log field definitions
 */

import { BaseFieldDefinition } from '../../shared/types/fieldRegistry';
import { createFieldHelpers } from '../../shared/utils/fieldRegistryHelpers';
import { AuditLog } from '../../services/api/domains/audit/types';

// Type alias for convenience and type safety
type AuditLogFieldDefinition = BaseFieldDefinition<AuditLog>;

/**
 * Comprehensive field registry for AuditLogs
 * This is the single source of truth for all field metadata
 * Note: AuditLogs are read-only, no edit functionality
 */
export const AUDIT_LOGS_FIELDS_REGISTRY: AuditLogFieldDefinition[] = [
  // ========== Core Audit Information ==========
  {
    key: 'table_name',
    label: 'Table',
    dataType: 'selection',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    selectionOptions: [
      { value: 'Audit_Log', label: 'Audit Log' },
      { value: 'Facilities', label: 'Facilities' },
      { value: 'Facilities_Aliases', label: 'Facilities Aliases' },
      { value: 'Grades', label: 'Grades' },
      { value: 'Grades_Aliases', label: 'Grades Aliases' },
      { value: 'Manufacturer_Facilities', label: 'Manufacturer Facilities' },
      { value: 'Manufacturers', label: 'Manufacturers' },
      { value: 'Manufacturers_Aliases', label: 'Manufacturers Aliases' },
      { value: 'Products', label: 'Products' },
      { value: 'Products_Facilities', label: 'Products Facilities' },
      { value: 'Products_Grades', label: 'Products Grades' },
      { value: 'Products_Manufacturers', label: 'Products Manufacturers' },
      { value: 'Products_ReplacedRns', label: 'Products Replaced RNs' },
      { value: 'Products_Synonyms', label: 'Products Synonyms' },
      {
        value: 'Products_TherapeuticAreas',
        label: 'Products Therapeutic Areas',
      },
      { value: 'Schema_Versions', label: 'Schema Versions' },
      { value: 'TherapeuticAreas_Aliases', label: 'Therapeutic Areas Aliases' },
      { value: 'therapeutic_areas', label: 'Therapeutic Areas' },
    ],
    renderConfig: {
      width: 120,
    },
  },
  {
    key: 'record_id',
    label: 'Record ID',
    dataType: 'number',
    primitiveType: 'number',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 100,
    },
  },
  {
    key: 'operation_type',
    label: 'Operation',
    dataType: 'selection',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    selectionOptions: [
      { value: 'INSERT', label: 'Insert' },
      { value: 'UPDATE', label: 'Update' },
      { value: 'DELETE', label: 'Delete' },
      { value: 'MERGE', label: 'Merge' },
    ],
    renderConfig: {
      width: 120,
    },
    description: 'Type of operation: INSERT, UPDATE, DELETE, or MERGE',
  },
  {
    key: 'field_name',
    label: 'Field',
    dataType: 'selection',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    selectionOptions: [
      { value: 'address_line1', label: 'Address Line 1' },
      { value: 'address_line2', label: 'Address Line 2' },
      { value: 'alias_id', label: 'Alias ID' },
      { value: 'applied_by', label: 'Applied By' },
      { value: 'applied_date', label: 'Applied Date' },
      { value: 'audit_date', label: 'Audit Date' },
      { value: 'audit_id', label: 'Audit ID' },
      { value: 'audit_type', label: 'Audit Type' },
      { value: 'canonical_facility_id', label: 'Canonical Facility ID' },
      { value: 'canonical_grade_id', label: 'Canonical Grade ID' },
      { value: 'canonical_id', label: 'Canonical ID' },
      {
        value: 'canonical_manufacturer_id',
        label: 'Canonical Manufacturer ID',
      },
      { value: 'canonical_smile', label: 'Canonical SMILE' },
      {
        value: 'canonical_therapeutic_area_id',
        label: 'Canonical Therapeutic Area ID',
      },
      { value: 'cas_number', label: 'CAS Number' },
      { value: 'change_reason', label: 'Change Reason' },
      { value: 'changed_by', label: 'Changed By' },
      { value: 'changed_date', label: 'Changed Date' },
      { value: 'checksum', label: 'Checksum' },
      { value: 'city', label: 'City' },
      { value: 'comments', label: 'Comments' },
      { value: 'country_code', label: 'Country Code' },
      { value: 'created_date', label: 'Created Date' },
      { value: 'deprecated_name', label: 'Deprecated Name' },
      { value: 'description', label: 'Description' },
      { value: 'duns_number', label: 'DUNS Number' },
      { value: 'execution_time_ms', label: 'Execution Time (ms)' },
      { value: 'facility_id', label: 'Facility ID' },
      { value: 'facility_name', label: 'Facility Name' },
      { value: 'facility_status', label: 'Facility Status' },
      { value: 'fei_number', label: 'FEI Number' },
      { value: 'field_name', label: 'Field Name' },
      { value: 'grade_id', label: 'Grade ID' },
      { value: 'grade_name', label: 'Grade Name' },
      { value: 'img', label: 'Image' },
      { value: 'inchi', label: 'InChI' },
      { value: 'inchi_key', label: 'InChI Key' },
      { value: 'is_active', label: 'Is Active' },
      { value: 'is_primary', label: 'Is Primary' },
      { value: 'latitude', label: 'Latitude' },
      { value: 'longitude', label: 'Longitude' },
      { value: 'manufacturer_id', label: 'Manufacturer ID' },
      { value: 'manufacturer_name', label: 'Manufacturer Name' },
      { value: 'manufacturing_status', label: 'Manufacturing Status' },
      { value: 'merge_reason', label: 'Merge Reason' },
      { value: 'merged_by', label: 'Merged By' },
      { value: 'merged_date', label: 'Merged Date' },
      { value: 'modified_date', label: 'Modified Date' },
      { value: 'molecular_formula', label: 'Molecular Formula' },
      { value: 'molecular_mass', label: 'Molecular Mass' },
      { value: 'new_value', label: 'New Value' },
      { value: 'note', label: 'Note' },
      { value: 'old_value', label: 'Old Value' },
      { value: 'operation_type', label: 'Operation Type' },
      { value: 'postal_code', label: 'Postal Code' },
      { value: 'preferred_product_name', label: 'Preferred Product Name' },
      { value: 'product_id', label: 'Product ID' },
      { value: 'product_name', label: 'Product Name' },
      { value: 'product_synonym', label: 'Product Synonym' },
      { value: 'qta_effective_date', label: 'QTA Effective Date' },
      { value: 'qta_expiration_date', label: 'QTA Expiration Date' },
      { value: 'qta_status', label: 'QTA Status' },
      { value: 'record_id', label: 'Record ID' },
      { value: 'replaced_rn', label: 'Replaced RN' },
      { value: 'smile', label: 'SMILE' },
      { value: 'state_province', label: 'State/Province' },
      { value: 'synonym_hash', label: 'Synonym Hash' },
      { value: 'synonym_id', label: 'Synonym ID' },
      { value: 'table_name', label: 'Table Name' },
      { value: 'therapeutic_area_id', label: 'Therapeutic Area ID' },
      { value: 'therapeutic_area_name', label: 'Therapeutic Area Name' },
      { value: 'uri', label: 'URI' },
      { value: 'user_id', label: 'User ID' },
      { value: 'version_id', label: 'Version ID' },
      { value: 'version_number', label: 'Version Number' },
    ],
    renderConfig: {
      width: 150,
    },
    description: 'Name of the field that was changed (null for INSERT/DELETE)',
  },

  // ========== Value Changes ==========
  {
    key: 'old_value',
    label: 'Old Value',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 250,
      flex: 1,
    },
  },
  {
    key: 'new_value',
    label: 'New Value',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 250,
      flex: 1,
    },
  },

  // ========== Audit Metadata ==========
  {
    key: 'changed_by',
    label: 'Changed By',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 200,
    },
  },
  {
    key: 'changed_date',
    label: 'Changed Date',
    dataType: 'date',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 180,
    },
  },
  {
    key: 'change_reason',
    label: 'Change Reason',
    dataType: 'string',
    primitiveType: 'string',
    isSortable: true,
    isFilterable: true,
    isSearchable: true,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 250,
    },
  },

  // ========== Internal ID (not displayed in grid by default) ==========
  {
    key: 'audit_id',
    label: 'Audit ID',
    dataType: 'number',
    primitiveType: 'number',
    isSortable: true,
    isFilterable: true,
    isSearchable: false,
    isEditable: false,
    isReadOnly: true,
    renderConfig: {
      width: 100,
    },
    description: 'Internal audit log ID',
  },
];

// Create all helper functions at once using shared factory
const helpers = createFieldHelpers<AuditLog>(AUDIT_LOGS_FIELDS_REGISTRY);

// Export individual helpers for convenient importing
export const getFieldByKey = helpers.getFieldByKey;
export const getSearchableFields = helpers.getSearchableFields;
export const getFilterableFields = helpers.getFilterableFields;
export const getSortableFields = helpers.getSortableFields;
export const getEditableFields = helpers.getEditableFields;
export const getRelationFields = helpers.getRelationFields;
export const getComputedFields = helpers.getComputedFields;
export const getReadOnlyFields = helpers.getReadOnlyFields;
export const getRequiredFields = helpers.getRequiredFields;
